import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Axios } from '../../../api';
import Loading from '../../../components/Loading';
import { DESENDING_ORDER, LISTING_LIMIT, DATE_FORMAT_SERVER, DATE_MONTHLY, DATE_WEEKLY, DATE_YEARLY } from '../../../constants';
import { DateTime } from 'luxon';
import axios from 'axios';
import { customStyles, toCommaSeparated } from '../../../utils';
import LineChart from './TrendGraph';
import DistributionChart from './DistributionChart';
import ExportExcel from './Actions/ExportExcel';
import TableComponent from './Table';

const CandidatesData = ({ filterData, appliedFilters }) => {
    let offset = 0;

    const [loading, setLoading] = useState(true);
    const [loadingOnlyTable, setLoadingOnlyTable] = useState(false);

    const [error, setError] = useState(false);
    const [errorOnlyTable, setErrorOnlyTable] = useState(false);

    const [data, setData] = useState(false);
    const [dashboardStats, setDashboardStats] = useState(false);

    const [perPage, setPerPage] = useState(LISTING_LIMIT);
    const [count, setCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);

    const [sort, setSort] = useState({ title: "created_on", order: DESENDING_ORDER })
    const [search, setSearch] = useState("");

    let cancel_request;

    // Build shared query params from global filters
    const { job, department, location, dateRanges, dateType } = appliedFilters;
    const dateRangesParam = encodeURIComponent(JSON.stringify(dateRanges || []));
    const ifSpontaneousApplicants = (job || []).some(item => item.value === 'spontaneous_flag');
    const jobIds = (job || []).filter(item => item.value !== 'spontaneous_flag').map(item => item.value).join(',') || "";

    // Duration display: memoized so it only recomputes when date selection/type changes (avoids delay on Apply)
    const durationProps = useMemo(() => {
        if (!dateRanges?.length) return null;
        if (dateType === DATE_WEEKLY.value) {
            if (dateRanges.length === 1) {
                const start = DateTime.fromISO(dateRanges[0].start).startOf('week');
                const end = DateTime.fromISO(dateRanges[0].end).endOf('week');
                return {
                    dateType: 'week',
                    dateRangeLabel: `W${start.weekNumber} (${start.toFormat('dd/MM')} - ${end.toFormat('dd/MM')}) ${start.toFormat('yyyy')}`,
                    dateRangeTooltip: null,
                    dateRangeDetail: null,
                    monthsCount: null,
                };
            }
            const weekLabels = dateRanges.map(r => {
                const start = DateTime.fromISO(r.start).startOf('week');
                return `W${start.weekNumber}`;
            });
            const weekDetailLines = dateRanges.map(r => {
                const start = DateTime.fromISO(r.start).startOf('week');
                const end = DateTime.fromISO(r.end).endOf('week');
                return `W${start.weekNumber} (${start.toFormat('dd/MM')} - ${end.toFormat('dd/MM')}) ${start.toFormat('yyyy')}`;
            });
            const maxInline = 3;
            const weekPreview = weekLabels.slice(0, maxInline).join(', ');
            return {
                dateType: 'week',
                dateRangeLabel: `Duration: ${dateRanges.length} weeks selected: ${weekPreview}`,
                dateRangeTooltip: null,
                dateRangeDetail: weekDetailLines.join('\n'),
                monthsCount: null,
            };
        }
        if (dateType === DATE_MONTHLY.value) {
            const months = dateRanges.map(r => DateTime.fromISO(r.start).toFormat('MMM yyyy'));
            if (dateRanges.length === 1) {
                return {
                    dateType: 'month',
                    dateRangeLabel: months[0],
                    dateRangeTooltip: null,
                    dateRangeDetail: null,
                    monthsCount: null,
                };
            }
            const first = DateTime.fromISO(dateRanges.reduce((min, r) => (r.start < min ? r.start : min), dateRanges[0].start)).toFormat('MMM yyyy');
            const last = DateTime.fromISO(dateRanges.reduce((max, r) => (r.end > max ? r.end : max), dateRanges[0].end)).toFormat('MMM yyyy');
            return {
                dateType: 'month',
                dateRangeLabel: `${first} – ${last}`,
                dateRangeTooltip: null,
                dateRangeDetail: months.join(', '),
                monthsCount: dateRanges.length,
            };
        }
        if (dateType === DATE_YEARLY.value) {
            const years = dateRanges.map(r => DateTime.fromISO(r.start).toFormat('yyyy'));
            if (dateRanges.length === 1) {
                return {
                    dateType: 'year',
                    dateRangeLabel: `Duration: ${years[0]}`,
                    dateRangeTooltip: null,
                    dateRangeDetail: null,
                    monthsCount: null,
                };
            }
            const maxInline = 3;
            const yearPreview = years.slice(0, maxInline).join(', ');
            return {
                dateType: 'year',
                dateRangeLabel: `Duration: ${yearPreview}`,
                dateRangeTooltip: null,
                dateRangeDetail: years.join('\n'),
                monthsCount: null,
            };
        }
        const start = DateTime.fromISO(dateRanges.reduce((min, r) => (r.start < min ? r.start : min), dateRanges[0].start)).toFormat('dd/MM/yyyy');
        const end = DateTime.fromISO(dateRanges.reduce((max, r) => (r.end > max ? r.end : max), dateRanges[0].end)).toFormat('dd/MM/yyyy');
        return {
            dateType: null,
            dateRangeLabel: `${start} – ${end}`,
            dateRangeTooltip: null,
            dateRangeDetail: null,
            monthsCount: null,
        };
    }, [dateRangesParam]);

    const getOnlyTableData = async () => {
        if (cancel_request) {
            return cancel_request.cancel('Api canceled due to new request.');
        }

        cancel_request = axios.CancelToken.source();

        setLoadingOnlyTable(true);
        setErrorOnlyTable(false);
        if (count) {
            offset = currentPage * perPage - perPage;
        } else {
            offset = 0;
        }
        try {
            const result = await Axios.get(`api/careers/candidate/dashboard/page/?offset=${offset}&limit=${perPage}${ifSpontaneousApplicants ? '&is_spontaneous=true' : ''}&search=${search}&job_ids=${jobIds}&order_column=${sort.title}&order=${sort.order}&date_ranges=${dateRangesParam}&department_ids=${department?.value || ""}&locations=${location?.value || ""}&is_duplicate=${!appliedFilters.removeDuplicates}`, {
                cancelToken: cancel_request.token
            })

            setData(result.data.payload);
            setCount(result.data.count);
            setLoadingOnlyTable(false)
            cancel_request = null

        } catch (e) {
            if (e.response) {
                setErrorOnlyTable(JSON.stringify(e.response.data.description));
            } else if (axios.isCancel) {

            }
            else {
                setErrorOnlyTable("Network Error");
            }
            setLoadingOnlyTable(false)
        }
    }

    const getData = async () => {
        console.log("getData called", appliedFilters);
        if (cancel_request) {
            return cancel_request.cancel('Api canceled due to new request.');
        }

        cancel_request = axios.CancelToken.source();

        setLoading(true);
        setError(false);
        if (count) {
            offset = currentPage * perPage - perPage;
        } else {
            offset = 0;
        }
        try {
            const [taleDataRes, dashboardStatsRes] = await Promise.all([
                Axios.get(`api/careers/candidate/dashboard/page/?offset=${offset}&limit=${perPage}${ifSpontaneousApplicants ? '&is_spontaneous=true' : ''}&search=${search}&job_ids=${jobIds}&order_column=${sort.title}&order=${sort.order}&date_ranges=${dateRangesParam}&department_ids=${department?.value || ""}&locations=${location?.value || ""}&is_duplicate=${!appliedFilters.removeDuplicates}`, {
                    cancelToken: cancel_request.token
                }),
                Axios.get(`api/careers/candidate/dashboard?job_ids=${jobIds}${ifSpontaneousApplicants ? '&is_spontaneous=true' : ''}&department_ids=${department?.value || ""}&date_ranges=${dateRangesParam}&locations=${location?.value || ""}&is_duplicate=${!appliedFilters.removeDuplicates}`,
                    { cancelToken: cancel_request.token }),
            ])
            setDashboardStats(dashboardStatsRes.data.payload);

            setData(taleDataRes.data.payload);
            setCount(taleDataRes.data.count);
            setLoading(false)
            cancel_request = null

        } catch (e) {
            if (e.response) {
                setError(JSON.stringify(e.response.data.description));
            } else if (axios.isCancel) {

            }
            else {
                setError("Network Error");
            }
            setLoading(false)
        }
    }

    useEffect(() => {
        getData();
        return () => {
            if (cancel_request) cancel_request.cancel('Component unmounted');
        };
    }, [perPage, sort, appliedFilters]);

    const handleSort = (data, order) => {
        setSort({
            title: data.selector,
            order: order,
        })
    }

    return (
        <>
            {
                loading && !error ?
                    <Loading />
                    :
                    !loading && error ?
                        <p className='text-red-500 text-center'>{error}</p>
                        :
                        <>
                            <div className=' flex justify-end my-4'>
                                <ExportExcel
                                    className={'!w-36'}
                                    text={'Export Report'}
                                    url={`api/careers/candidate/report/?date_ranges=${dateRangesParam}&is_duplicate=${!appliedFilters.removeDuplicates}&export=xlsx`}
                                />
                            </div>
                            <div className='flex flex-col gap-11'>
                                <LineChart
                                    title={'Trend Analysis'}
                                    startDate={dateRanges?.length ? dateRanges.reduce((min, r) => r.start < min ? r.start : min, dateRanges[0].start) : undefined}
                                    endDate={dateRanges?.length ? dateRanges.reduce((max, r) => r.end > max ? r.end : max, dateRanges[0].end) : undefined}
                                    data={dashboardStats?.trend_data}
                                    durationProps={durationProps}
                                />
                                <DistributionChart
                                    title="Applications by Position"
                                    seriesName="Applications"
                                    categories={dashboardStats?.group_by_job?.map(item => item.job_id === null ? 'Spontaneouos Applications' : `${item.job_title}`) || []}
                                    seriesData={dashboardStats?.group_by_job?.map(item => item.total_count) || []}
                                    durationProps={durationProps}
                                />
                                <DistributionChart
                                    title="Applications by Location"
                                    durationProps={durationProps}
                                    stacked
                                    categories={dashboardStats?.group_by_location[0]?.job__location !== undefined ? dashboardStats?.group_by_location?.map(item => item.job__location === null ? 'Spontaneouos Applications' : `${item.job__location}`) : dashboardStats?.group_by_location?.map(item => item.location) || []}
                                    series={
                                        (() => {
                                            const data = dashboardStats?.group_by_location || [];
                                            if (data.length === 0) return [];

                                            if (data[0].job__location !== undefined && data[0].jobs === undefined) {
                                                return [{
                                                    name: "Applications",
                                                    data: data.map(item => item.total_count || 0)
                                                }];
                                            }

                                            const jobTitles = Array.from(
                                                new Set(data.flatMap(loc => loc.jobs?.map(j => j.job__title) || []))
                                            );

                                            return jobTitles.map(title => ({
                                                name: title,
                                                data: data.map(location => {
                                                    const jobMatch = location.jobs?.find(j => j.job__title === title);
                                                    return jobMatch ? jobMatch.total_count : 0;
                                                })
                                            }));
                                        })()
                                    }
                                />
                            </div>

                            <div className=''>
                                <div className='flex gap-8 justify-between items-center mt-11 mb-4'>
                                    <h1 className='heading-sm'>Table</h1>
                                    <div className='flex gap-4'>
                                        <ExportExcel
                                            text={'Export Excel'}
                                            url={`api/careers/candidate/dashboard/page/?offset=${offset}&limit=${perPage}&search=${search}&job_ids=${jobIds}&order_column=${sort.title}&order=${sort.order}&date_ranges=${dateRangesParam}&department_ids=${department?.value || ""}&locations=${location?.value || ""}&is_duplicate=${!appliedFilters.removeDuplicates}&export=excel`}
                                        />
                                        <ExportExcel
                                            text={'Export CSV'}
                                            url={`api/careers/candidate/dashboard/page/?offset=${offset}&limit=${perPage}&search=${search}&job_ids=${jobIds}&order_column=${sort.title}&order=${sort.order}&date_ranges=${dateRangesParam}&department_ids=${department?.value || ""}&locations=${location?.value || ""}&is_duplicate=${!appliedFilters.removeDuplicates}&export=csv`}
                                        />
                                    </div>
                                </div>
                                {
                                    <>
                                        <TableComponent
                                            data={data}
                                            loading={loading}
                                            count={count}
                                            perPage={perPage}
                                            currentPage={currentPage}
                                            setCurrentPage={setCurrentPage}
                                            offset={offset}
                                            handleSort={handleSort}
                                            customStyles={customStyles}
                                            getOnlyTableData={getOnlyTableData}
                                            cancel_request={cancel_request}
                                            loadingOnlyTable={loadingOnlyTable}
                                            errorOnlyTable={errorOnlyTable}
                                            error={error}
                                        />
                                    </>
                                }
                            </div>
                        </>

            }
        </>
    )
}

export default CandidatesData